import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "@phosphor-icons/react";
import { SectionHead, Reveal } from "@/components/Reveal";
import { SYSTEM_NODES } from "@/data/content";

const NODE_SLUGS = {
    website: "bespoke-websites", ai: "ai-enquiry-agent", crm: "crm-automation",
    leads: "lead-generation-automation", sales: "ai-sales-agent", bookings: "ai-appointment-setter",
    seo: "seo-services", analytics: null, automation: "business-automation",
    support: "ai-customer-support", payments: "ecommerce-automation", social: null, security: "website-security",
};

export default function SystemMap() {
    const [active, setActive] = useState(SYSTEM_NODES[0]);
    const n = SYSTEM_NODES.length;
    const pos = SYSTEM_NODES.map((_, i) => {
        const angle = (-90 + (i * 360) / n) * (Math.PI / 180);
        return { x: 50 + 41 * Math.cos(angle), y: 50 + 42 * Math.sin(angle) };
    });

    return (
        <section id="system" className="relative border-t border-white/10 bg-void py-28 md:py-36" data-testid="system-section">
            <div className="mx-auto max-w-[1400px] px-5 md:px-10">
                <SectionHead
                    number="02"
                    label="The HIGHTS system"
                    title="One business. One connected system."
                    copy="Most businesses stitch together ten disconnected tools and suppliers. HIGHTS engineers everything — website, AI, CRM, bookings, payments, SEO, security — as one ecosystem where every part feeds the next. Select any node."
                />

                <Reveal className="mt-16 hidden md:block" y={30}>
                    <div className="relative mx-auto aspect-[16/10] max-w-5xl" data-testid="system-map">
                        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full" aria-hidden="true">
                            {pos.map((p, i) => (
                                <motion.line
                                    key={i}
                                    x1="50" y1="50" x2={p.x} y2={p.y}
                                    stroke={active === SYSTEM_NODES[i] ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.12)"}
                                    strokeWidth="0.35"
                                    vectorEffect="non-scaling-stroke"
                                    initial={{ pathLength: 0 }}
                                    whileInView={{ pathLength: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 1.4, delay: i * 0.08 }}
                                />
                            ))}
                        </svg>
                        <div className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
                            <div className="animate-node-pulse flex h-32 w-32 items-center justify-center rounded-full border border-white/30 bg-black">
                                <span className="font-display text-lg font-black tracking-[0.2em] text-white">HIGHTS</span>
                            </div>
                        </div>
                        {SYSTEM_NODES.map((node, i) => (
                            <button
                                key={node.key}
                                data-testid={`system-node-${node.key}`}
                                onClick={() => setActive(node)}
                                style={{ left: `${pos[i].x}%`, top: `${pos[i].y}%` }}
                                className={`absolute z-10 -translate-x-1/2 -translate-y-1/2 border px-4 py-2.5 font-mono text-[10px] tracking-[0.2em] transition-colors duration-300 ${
                                    active === node.key
                                        ? "border-white bg-white text-black"
                                        : "border-white/20 bg-black/80 text-white/65 hover:border-white/60 hover:text-white"
                                }`}
                            >
                                {node.label}
                            </button>
                        ))}
                    </div>
                </Reveal>

                <div className="mt-14 grid grid-cols-2 gap-2 sm:grid-cols-3 md:hidden">
                    {SYSTEM_NODES.map((node) => (
                        <button
                            key={node.key}
                            data-testid={`system-node-mobile-${node.key}`}
                            onClick={() => setActive(node)}
                            className={`border px-3 py-3 font-mono text-[10px] tracking-[0.15em] transition-colors ${
                                active === node.key ? "border-white bg-white text-black" : "border-white/15 text-white/60"
                            }`}
                        >
                            {node.label}
                        </button>
                    ))}
                </div>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={active.key}
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                        className="mx-auto mt-12 max-w-3xl border border-white/12 bg-surface p-8 md:p-10"
                        data-testid="system-node-panel"
                    >
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">System node</p>
                        <h3 className="mt-3 font-display text-2xl font-black tracking-tight text-white sm:text-3xl">{active.label}</h3>
                        <p className="mt-4 text-sm font-light leading-relaxed text-white/60 md:text-base">{active.blurb}</p>
                        {NODE_SLUGS[active.key] && (
                            <Link
                                to={`/services/${NODE_SLUGS[active.key]}`}
                                data-testid="system-node-link"
                                className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-white transition-colors hover:text-white/60"
                            >
                                Explore this service <ArrowUpRight size={15} />
                            </Link>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>
        </section>
    );
}
